import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { orderBoardService } from '../../services/orderBoardService';
import { OrderBoardProps } from '../../types/components';
import {
  OrderBoardCard,
  OrderBoard as OrderBoardType,
} from '../../types/orderBoard';
import BoardColumn from './BoardColumn';
import CardDetailsModal from './CardDetailsModal';
import CardStatusModal from './CardStatusModal';

const OrderBoard: React.FC<OrderBoardProps> = ({ board, onBoardUpdate }) => {
  const [_activeId, setActiveId] = useState<string | null>(null);
  const [draggedCard, setDraggedCard] = useState<OrderBoardCard | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<{
    cardId: number;
    targetColumnId: number;
    columnName: string;
  } | null>(null);
  const [isCardDetailsModalOpen, setIsCardDetailsModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<OrderBoardCard | null>(null);
  // Optimistic board state for smooth animations
  const [optimisticBoard, setOptimisticBoard] = useState<OrderBoardType | null>(
    null
  );
  // Track cards that are being moved to hide them in old column
  const [movingCardIds, setMovingCardIds] = useState<Set<number>>(new Set());
  // Track scrolling state for auto-hiding scrollbars
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Sync optimistic board when prop board changes (after API update)
  useEffect(() => {
    if (optimisticBoard && board.id === optimisticBoard.id) {
      // Only clear if the board structure matches (API update completed)
      setOptimisticBoard(null);
    }
  }, [board, optimisticBoard]);

  // Handle scroll events for auto-hiding scrollbars
  const handleScroll = () => {
    setIsScrolling(true);
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Configure sensors for both mouse and touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  // Handle card click (not drag)
  const handleCardClick = (cardId: number) => {
    if (board.isLocked) {
      return; // Don't open modal if board is locked
    }

    // Find the card in the current board state
    const card = displayBoard.columns
      .flatMap(col => col.cards)
      .find(c => c.id === cardId);

    if (card) {
      setSelectedCard(card);
      setIsCardDetailsModalOpen(true);
    }
  };

  // Handle card details update
  const handleCardDetailsUpdate = async (request: {
    targetColumnId?: number;
    notes?: string;
  }) => {
    if (!selectedCard) return;

    try {
      await orderBoardService.updateCardDetails(selectedCard.id, request);
      toast.success('Card details updated successfully');
      setIsCardDetailsModalOpen(false);
      setSelectedCard(null);
      onBoardUpdate?.();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Failed to update card details'
      );
      throw error;
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    // Prevent dragging if board is locked
    if (board.isLocked) {
      return;
    }

    setActiveId(event.active.id as string);
    const cardId = parseInt(event.active.id as string);
    const card = findCardById(cardId);
    if (card) {
      setDraggedCard(card);
    }
  };

  // Custom collision detection to prioritize columns over cards
  const collisionDetection = (args: any) => {
    // First, check if pointer is within any droppable (column)
    const pointerCollisions = pointerWithin(args);

    // Filter to only return column droppables (not cards)
    // Column IDs are prefixed with "column-"
    const columnCollisions = pointerCollisions.filter((collision: any) => {
      const id = collision.id.toString();
      return id.startsWith('column-');
    });

    if (columnCollisions.length > 0) {
      // Return the closest column (first one is typically the closest)
      return [columnCollisions[0]];
    }

    // Fallback to rect intersection, but filter to only columns
    const rectCollisions = rectIntersection(args);
    const filteredRectCollisions = rectCollisions.filter((collision: any) => {
      const id = collision.id.toString();
      return id.startsWith('column-');
    });

    if (filteredRectCollisions.length > 0) {
      return [filteredRectCollisions[0]];
    }

    // If no column collisions found, return empty array
    return [];
  };

  const handleDragOver = (event: DragOverEvent) => {
    // This helps with real-time feedback during drag
    const { over: _over } = event;
    // We don't need to do anything here, but having this handler
    // can help with collision detection accuracy
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      setDraggedCard(null);
      return;
    }

    const cardId = parseInt(active.id as string);
    const card = findCardById(cardId);
    if (!card) {
      setActiveId(null);
      setDraggedCard(null);
      return;
    }

    // Extract column ID from the over.id (which is prefixed with "column-")
    let targetColumnId: number | null = null;

    const overId = over.id.toString();
    if (overId.startsWith('column-')) {
      // Extract the numeric ID from "column-{id}"
      const columnIdStr = overId.replace('column-', '');
      targetColumnId = parseInt(columnIdStr);
    }

    if (!targetColumnId) {
      // If we didn't get a valid column ID, don't move the card
      setActiveId(null);
      setDraggedCard(null);
      return;
    }

    const targetColumn = (optimisticBoard || board).columns.find(
      c => c.id === targetColumnId
    );
    if (!targetColumn) {
      setActiveId(null);
      setDraggedCard(null);
      return;
    }

    // If moving to different column
    if (card.columnId !== targetColumnId) {
      // Optimistically update the board state for smooth animation
      const updatedBoard = updateBoardOptimistically(
        optimisticBoard || board,
        cardId,
        targetColumnId
      );
      setOptimisticBoard(updatedBoard);

      // Mark card as moving to hide it in old column during transition
      setMovingCardIds(new Set([cardId]));

      // Clear draggedCard first
      setDraggedCard(null);

      // Clear activeId after a short delay to allow optimistic update to render
      // This ensures the card appears in the new column before the drag ends
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setActiveId(null);
          // Clear moving state after transition completes
          setTimeout(() => {
            setMovingCardIds(new Set());
          }, 300);
        });
      });

      // For custom boards, skip the status update modal
      if (board.isCustom || !board.orderId) {
        try {
          await orderBoardService.moveCard(board.id, cardId, {
            targetColumnId,
          });
          toast.success('Card moved successfully');
          setOptimisticBoard(null);
          setMovingCardIds(new Set());
          onBoardUpdate?.();
        } catch (error: any) {
          toast.error(error.response?.data?.message || 'Failed to move card');
          // Revert optimistic update on error
          setOptimisticBoard(null);
          setMovingCardIds(new Set());
          onBoardUpdate?.();
        }
      } else {
        // Set pending move for modal
        setPendingMove({
          cardId,
          targetColumnId,
          columnName: targetColumn.name,
        });
        setIsModalOpen(true);
      }
    } else {
      // No move, just clear drag state
      setActiveId(null);
      setDraggedCard(null);
      setMovingCardIds(new Set());
    }
  };

  const handleModalConfirm = async (orderStatus?: string) => {
    if (!pendingMove) return;

    setIsModalOpen(false);
    const moveData = { ...pendingMove };
    setPendingMove(null);

    try {
      // Move the card
      await orderBoardService.moveCard(board.id, moveData.cardId, {
        targetColumnId: moveData.targetColumnId,
      });

      // Optionally update status (only for order boards)
      if (orderStatus && board.orderId) {
        await orderBoardService.updateCardStatus(board.id, moveData.cardId, {
          orderStatus,
        });
      }

      toast.success('Card moved successfully');
      setOptimisticBoard(null);
      setMovingCardIds(new Set());
      onBoardUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to move card');
      // Revert optimistic update on error
      setOptimisticBoard(null);
      setMovingCardIds(new Set());
      onBoardUpdate?.();
    }
  };

  const findCardById = (
    cardId: number,
    searchBoard?: OrderBoardType
  ): OrderBoardCard | null => {
    const boardToSearch = searchBoard || optimisticBoard || board;
    for (const column of boardToSearch.columns) {
      const card = column.cards.find(c => c.id === cardId);
      if (card) return card;
    }
    return null;
  };

  // Helper function to optimistically update board state
  const updateBoardOptimistically = (
    currentBoard: OrderBoardType,
    cardId: number,
    targetColumnId: number
  ): OrderBoardType => {
    const updatedColumns = currentBoard.columns.map(column => {
      // Remove card from source column
      if (column.cards.some(c => c.id === cardId)) {
        return {
          ...column,
          cards: column.cards.filter(c => c.id !== cardId),
        };
      }
      // Add card to target column
      if (column.id === targetColumnId) {
        const card = currentBoard.columns
          .flatMap(c => c.cards)
          .find(c => c.id === cardId);
        if (card) {
          return {
            ...column,
            cards: [...column.cards, { ...card, columnId: targetColumnId }],
          };
        }
      }
      return column;
    });

    return {
      ...currentBoard,
      columns: updatedColumns,
    };
  };

  // Use optimistic board if available, otherwise use prop board
  const displayBoard = optimisticBoard || board;

  const currentCardStatus = pendingMove
    ? board.columns.find(c => {
        const card = c.cards.find(card => card.id === pendingMove.cardId);
        return card !== undefined;
      })?.name
    : undefined;

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div
          className={`flex gap-2 md:gap-4 overflow-x-auto overflow-y-hidden pb-2 md:pb-4 scrollbar-auto-hide h-[calc(100vh-280px)] md:h-[calc(100vh-400px)] max-h-[750px] min-h-[350px] md:min-h-[500px] px-2 md:px-4 pt-2 md:pt-4 ${
            isScrolling ? 'scrolling' : ''
          }`}
          onScroll={handleScroll}
        >
          {displayBoard.columns.map(column => (
            <BoardColumn
              key={column.id}
              column={column}
              hideCardIds={movingCardIds}
              onCardClick={handleCardClick}
              isLocked={board.isLocked}
            />
          ))}
        </div>
        <DragOverlay>
          {draggedCard ? (
            <div className='rotate-3 shadow-2xl'>
              <div className='bg-white rounded-lg shadow-lg p-3 min-w-[250px] border-2 border-primary-500'>
                <h4 className='font-semibold text-sm text-gray-900'>
                  {draggedCard.title}
                </h4>
                {draggedCard.description && (
                  <p className='text-xs text-gray-500 mt-1'>
                    {draggedCard.description}
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <CardStatusModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setPendingMove(null);
          // Revert optimistic update if modal is cancelled
          setOptimisticBoard(null);
          setMovingCardIds(new Set());
          onBoardUpdate?.();
        }}
        onConfirm={handleModalConfirm}
        currentStatus={currentCardStatus}
        columnName={pendingMove?.columnName || ''}
      />

      <CardDetailsModal
        isOpen={isCardDetailsModalOpen}
        onClose={() => {
          setIsCardDetailsModalOpen(false);
          setSelectedCard(null);
        }}
        onConfirm={handleCardDetailsUpdate}
        card={selectedCard}
        columns={displayBoard.columns}
        isLocked={board.isLocked}
      />
    </>
  );
};

export default OrderBoard;
